// frontend-web/src/pages/AdminDashboard.jsx
import React, { useState, useEffect } from "react";
import api from "../api";
import { useNavigate } from "react-router-dom";
import PracticeSetQuestionsModal from "./Admin/PracticeSetQuestionsModal";

export default function AdminDashboard() {
  const nav = useNavigate();

  // ================= UI STATES =================
  const [showMenu, setShowMenu] = useState(false);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showStudyContentModal, setShowStudyContentModal] = useState(false);
  const [showPracticeSetModal, setShowPracticeSetModal] = useState(false);
  const [showScholarshipModal, setShowScholarshipModal] = useState(false);

  // Practice set flow
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [currentPracticeSet, setCurrentPracticeSet] = useState(null);

  // ================= DATA =================
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // ================= PRACTICE SET FORM =================
  const [practiceName, setPracticeName] = useState("");
  const [practiceClassId, setPracticeClassId] = useState("");
  const [practiceSubjectId, setPracticeSubjectId] = useState("");
  const [practiceNumQuestions, setPracticeNumQuestions] = useState(10);
  const [practiceDuration, setPracticeDuration] = useState(20);

  // Form States
  const [qClassId, setQClassId] = useState("");
  const [qSubjectId, setQSubjectId] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [optionA, setOptionA] = useState("");
  const [optionB, setOptionB] = useState("");
  const [optionC, setOptionC] = useState("");
  const [optionD, setOptionD] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState("A");

  const [studyTitle, setStudyTitle] = useState("");
  const [studyClassId, setStudyClassId] = useState("");
  const [studySubjectId, setStudySubjectId] = useState("");
  const [studyText, setStudyText] = useState("");
  const [studyFile, setStudyFile] = useState(null);

  const [scholarshipTitle, setScholarshipTitle] = useState("");
  const [scholarshipType, setScholarshipType] = useState("regular");
  const [scholarshipClassId, setScholarshipClassId] = useState("");
  const [scholarshipSubjectId, setScholarshipSubjectId] = useState("");
  const [scholarshipNumQuestions, setScholarshipNumQuestions] = useState(10);
  const [scholarshipDuration, setScholarshipDuration] = useState(600);
  const [scholarshipDate, setScholarshipDate] = useState("");
  const [scholarshipTime, setScholarshipTime] = useState("");

  // ================= LOAD DATA =================
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return nav("/login");
    loadData();
  }, [nav]);

  const loadData = async () => {
    try {
      const [cRes, sRes] = await Promise.all([
        api.get("/admin/classes").catch(() => ({ data: [] })),
        api.get("/admin/subjects").catch(() => ({ data: [] }))
      ]);
      setClasses(cRes.data || []);
      setSubjects(sRes.data || []);
    } catch (err) {
      console.error("Load data failed", err);
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    nav("/login");
  };

  // Add Class / Subject
  const addClass = () => {
    const name = prompt("Enter Class Name:");
    if (name) {
      api.post("/admin/classes", { name }).then(() => {
        alert("Class Added");
        loadData();
      }).catch(err => alert(err.response?.data?.msg || "Error"));
    }
  };

  const addSubject = () => {
    const name = prompt("Enter Subject Name:");
    if (!name) return;
    const classId = prompt(`Enter Class ID (Available: ${classes.map(c => `${c.name}(${c.id})`).join(", ")})`);
    if (classId) {
      api.post("/admin/subjects", { name, class_id: parseInt(classId) })
        .then(() => { alert("Subject Added"); loadData(); })
        .catch(err => alert(err.response?.data?.msg || "Error"));
    }
  };

  // CSV Upload
  const handleCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    api.post("/admin/upload_questions_csv", form)
      .then(() => alert("Uploaded Successfully"))
      .catch(err => alert(err.response?.data?.msg || "Upload Failed"));
  };

  // Add Question
  const addQuestion = () => {
    if (!qClassId || !qSubjectId || !questionText) return alert("Fill all fields");
    const payload = {
      question_type: "objective",
      text: questionText,
      option_a: optionA,
      option_b: optionB,
      option_c: optionC,
      option_d: optionD,
      correct: correctAnswer,
      class_id: parseInt(qClassId),
      subject_id: parseInt(qSubjectId)
    };
    api.post("/admin/questions", payload)
      .then(() => {
        alert("Question Added");
        setShowQuestionForm(false);
        setQuestionText(""); setOptionA(""); setOptionB(""); setOptionC(""); setOptionD("");
      })
      .catch(err => alert(err.response?.data?.msg || "Error"));
  };

  // Study Content
  const submitStudyContent = () => {
    if (!studyTitle || !studyClassId || !studySubjectId) return alert("Fill all fields");
    const fd = new FormData();
    fd.append("title", studyTitle);
    fd.append("class_id", studyClassId);
    fd.append("subject_id", studySubjectId);
    fd.append("content_text", studyText);
    if (studyFile) fd.append("file", studyFile);
    api.post("/admin/study-content", fd)
      .then(() => { alert("Content Added"); setShowStudyContentModal(false); })
      .catch(err => alert(err.response?.data?.msg || "Error"));
  };

  // ================= PRACTICE SET CREATE (FIXED) =================
  const createPracticeSet = async () => {
    if (!practiceName || !practiceClassId || !practiceSubjectId) {
      alert("All fields are required!");
      return;
    }

    try {
      const res = await api.post("/admin/practice_set", {
        name: practiceName.trim(),
        class_id: Number(practiceClassId),
        subject_id: Number(practiceSubjectId),
        num_questions: Number(practiceNumQuestions),
        duration_minutes: Number(practiceDuration),
      });

      // 🔥 ONLY correct ID source
      const psId = res.data.practice_set_id;
      if (!psId) throw new Error("practice_set_id missing");

      setCurrentPracticeSet({
        id: psId,
        name: practiceName,
        num_questions: Number(practiceNumQuestions),
        questions: res.data.questions || [], // seed questions
      });

      setShowPracticeSetModal(false);
      setShowQuestionModal(true);

    } catch (err) {
      alert(err.response?.data?.error || "Failed to create Practice Set");
    }
  };

  // Scholarship Exam
  const createScholarshipExam = () => {
    if (!scholarshipTitle || !scholarshipClassId || !scholarshipSubjectId || !scholarshipDate || !scholarshipTime) {
      return alert("All fields required");
    }
    const payload = {
      title: scholarshipTitle,
      exam_type: scholarshipType,
      class_id: parseInt(scholarshipClassId),
      subject_id: parseInt(scholarshipSubjectId),
      total_questions: parseInt(scholarshipNumQuestions),
      duration_seconds: parseInt(scholarshipDuration),
      exam_date: scholarshipDate,
      start_time: scholarshipTime
    };
    api.post("/admin/scholarship_exam", payload)
      .then(() => alert("Exam Created"))
      .catch(err => alert(err.response?.data?.msg || "Error"));
  };

  // ================= UI =================
  return (
    <div style={{ fontFamily: "Inter, sans-serif", background: "#f9fafb", minHeight: "100vh" }}>
      {/* HEADER */}
      <header style={headerStyle}>
        <h2>EduAt10 – Super Admin</h2>
        <div style={{ position: "relative" }}>
          <div style={avatarStyle} onClick={() => setShowMenu(!showMenu)}>👤 Admin</div>
          {showMenu && (
            <div style={menuStyle}>
              <button style={menuBtn}>👤 Profile</button>
              <button style={menuBtn}>🎟️ Subscription</button>
              <button style={menuBtn} onClick={() => nav("/admin/support")}>🆘 Support</button>
              <button style={{ ...menuBtn, color: "#dc2626" }} onClick={logout}>🚪 Logout</button>
            </div>
          )}
        </div>
      </header>

      {/* DASHBOARD */}
      <div style={{ padding: 40 }}>
        <h3>Super Admin Dashboard</h3>
        <div style={cardGrid}>
          <div style={card} onClick={() => setShowClassModal(true)}>Add Classes & Subjects</div>
          <div style={card} onClick={() => setShowUploadModal(true)}>Upload Questions (CSV)</div>
          <div style={card} onClick={() => setShowQuestionForm(true)}>Add Question Manually</div>
          <div style={card} onClick={() => nav("/admin/view-questions")}>View Questions</div>
          <div style={card} onClick={() => setShowStudyContentModal(true)}>Add Study Content</div>
          <div style={card} onClick={() => nav("/admin/study-content-list")}>📚 View Study Content</div>
          <div style={card} onClick={() => setShowPracticeSetModal(true)}>Create Practice Set</div>
          <div style={card} onClick={() => nav("/admin/practice-set-list")}>View Practice Sets</div>
          <div style={card} onClick={() => nav("/admin/case-study-practice")}>📘 Create Case Study Practice</div>
          <div style={card} onClick={() => nav("/admin/case-study-practice/list")}>📘 Case Study Practice List</div>
          <div style={card} onClick={() => setShowScholarshipModal(true)}>Create Scholarship Exam</div>
          <div style={card} onClick={() => nav("/admin/case-study-questions")}>Case Study Management</div>
          <div style={card} onClick={() => nav("/admin/create-mega-scholarship")}>Create Mega Scholarship Exam</div>
          <div style={card} onClick={() => nav("/admin/mega-scholarship-list")}>View List Mega Scholarship Exam</div>
          <div style={card} onClick={() => nav("/admin/users")}>👤 View Users</div>
          <div style={card} onClick={() => nav("/admin/reasoning")}>🧠 Add & manage reasoning questions</div>
          <div style={card} onClick={() => nav("/admin/reasoning/list")}>🧠 List reasoning questions</div>
           <div style={card} onClick={() => nav("/admin/support")}>View Support Ticket</div>
          
        </div>
      </div>
      
      {/* All Modals */}
      {showClassModal && (
        <Modal title="Add Class / Subject" onClose={() => setShowClassModal(false)}>
          <button style={btnPrimary} onClick={addClass}>Add Class</button>
          <button style={btnSecondary} onClick={addSubject}>Add Subject</button>
        </Modal>
      )}

      {showUploadModal && (
        <Modal title="Upload CSV" onClose={() => setShowUploadModal(false)}>
          <input type="file" accept=".csv" onChange={handleCSV} />
        </Modal>
      )}

      {showQuestionForm && (
        <Modal title="Add Question" onClose={() => setShowQuestionForm(false)}>
          <select style={input} value={qClassId} onChange={e => setQClassId(e.target.value)}>
            <option value="">Select Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={input} value={qSubjectId} onChange={e => setQSubjectId(e.target.value)}>
            <option value="">Select Subject</option>
            {subjects.filter(s => s.class_id === Number(qClassId)).map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input style={input} placeholder="Question" value={questionText} onChange={e => setQuestionText(e.target.value)} />
          <input style={input} placeholder="A" value={optionA} onChange={e => setOptionA(e.target.value)} />
          <input style={input} placeholder="B" value={optionB} onChange={e => setOptionB(e.target.value)} />
          <input style={input} placeholder="C" value={optionC} onChange={e => setOptionC(e.target.value)} />
          <input style={input} placeholder="D" value={optionD} onChange={e => setOptionD(e.target.value)} />
          <select style={input} value={correctAnswer} onChange={e => setCorrectAnswer(e.target.value)}>
            <option value="A">A</option><option value="B">B</option><option value="C">C</option><option value="D">D</option>
          </select>
          <button style={btnPrimary} onClick={addQuestion}>Save Question</button>
        </Modal>
      )}

      {showStudyContentModal && (
        <Modal title="Add Study Content" onClose={() => setShowStudyContentModal(false)}>
          <input style={input} placeholder="Title" value={studyTitle} onChange={e => setStudyTitle(e.target.value)} />
          <select style={input} value={studyClassId} onChange={e => setStudyClassId(e.target.value)}>
            <option value="">Select Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={input} value={studySubjectId} onChange={e => setStudySubjectId(e.target.value)}>
            <option value="">Select Subject</option>
            {subjects.filter(s => s.class_id === Number(studyClassId)).map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <textarea style={{ ...input, height: 100 }} placeholder="Content" value={studyText} onChange={e => setStudyText(e.target.value)} />
          <input type="file" onChange={e => setStudyFile(e.target.files[0])} />
          <button style={btnPrimary} onClick={submitStudyContent}>Upload</button>
        </Modal>
      )}

      {/* PRACTICE SET MODAL */}
      {showPracticeSetModal && (
        <Modal title="Create Practice Set" onClose={() => setShowPracticeSetModal(false)}>
          <input style={input} placeholder="Practice Set Name" value={practiceName} onChange={e => setPracticeName(e.target.value)} />
          <select style={input} value={practiceClassId} onChange={e => setPracticeClassId(e.target.value)}>
            <option value="">Select Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={input} value={practiceSubjectId} onChange={e => setPracticeSubjectId(e.target.value)}>
            <option value="">Select Subject</option>
            {subjects.filter(s => s.class_id === Number(practiceClassId))
              .map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input style={input} type="number" placeholder="No. of Questions" value={practiceNumQuestions} onChange={e => setPracticeNumQuestions(e.target.value)} />
          <input style={input} type="number" placeholder="Duration (min)" value={practiceDuration} onChange={e => setPracticeDuration(e.target.value)} />
          <button style={btnPrimary} onClick={createPracticeSet}>Create & Select Questions</button>
        </Modal>
      )}

      {/* Scholarship Exam */}
      {showScholarshipModal && (
        <Modal title="Create Scholarship Exam" onClose={() => setShowScholarshipModal(false)}>
          <input style={input} placeholder="Exam Title" value={scholarshipTitle} onChange={e => setScholarshipTitle(e.target.value)} />
          <select style={input} value={scholarshipType} onChange={e => setScholarshipType(e.target.value)}>
            <option value="regular">Regular</option>
            <option value="mega">Mega</option>
          </select>
          <select style={input} value={scholarshipClassId} onChange={e => setScholarshipClassId(e.target.value)}>
            <option value="">Select Class</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select style={input} value={scholarshipSubjectId} onChange={e => setScholarshipSubjectId(e.target.value)}>
            <option value="">Select Subject</option>
            {subjects.filter(s => s.class_id === Number(scholarshipClassId)).map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input style={input} type="number" placeholder="Questions" value={scholarshipNumQuestions} onChange={e => setScholarshipNumQuestions(e.target.value)} />
          <input style={input} type="number" placeholder="Duration (sec)" value={scholarshipDuration} onChange={e => setScholarshipDuration(e.target.value)} />
          <input style={input} type="date" value={scholarshipDate} onChange={e => setScholarshipDate(e.target.value)} />
          <input style={input} type="time" value={scholarshipTime} onChange={e => setScholarshipTime(e.target.value)} />
          <button style={btnPrimary} onClick={createScholarshipExam}>Create Exam</button>
        </Modal>
      )}

      {/* QUESTION SELECTION MODAL */}
      <PracticeSetQuestionsModal
        show={showQuestionModal}
        practiceSet={currentPracticeSet}
        onClose={() => {
          setShowQuestionModal(false);
          setCurrentPracticeSet(null);
        }}
      />
    </div>
  );
}

// ================= SHARED COMPONENTS & STYLES =================
const Modal = ({ title, children, onClose }) => (
  <div style={modalOverlay}>
    <div style={modalContent}>
      <h3>{title}</h3>
      {children}
      <button style={btnClose} onClick={onClose}>Close</button>
    </div>
  </div>
);

const headerStyle = { background: "#1e293b", color: "white", padding: "16px 32px", display: "flex", justifyContent: "space-between", alignItems: "center" };
const avatarStyle = { padding: "8px 16px", background: "#334155", borderRadius: 20, cursor: "pointer" };
const menuStyle = { position: "absolute", right: 0, top: 50, background: "white", borderRadius: 8, boxShadow: "0 4px 20px rgba(0,0,0,0.2)" };
const menuBtn = { padding: "12px 20px", width: "100%", border: "none", background: "none", cursor: "pointer" };
const cardGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24, marginTop: 24 };
const card = { background: "white", padding: 28, borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.1)", textAlign: "center", cursor: "pointer", fontWeight: 600 };
const input = { width: "100%", padding: 12, margin: "8px 0", borderRadius: 8, border: "1px solid #cbd5e1" };
const btnPrimary = { padding: "12px 24px", background: "#2563eb", color: "white", border: "none", borderRadius: 8, cursor: "pointer", marginTop: 16 };
const btnSecondary = {
  padding: "12px 24px",
  background: "#e5e7eb",   // light gray
  color: "#111827",
  border: "1px solid #d1d5db",
  borderRadius: 8,
  cursor: "pointer",
  marginTop: 12
};

const btnClose = { marginTop: 20, padding: "10px 20px", background: "#ef4444", color: "white", border: "none", borderRadius: 8 };
const modalOverlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalContent = { background: "white", padding: 32, borderRadius: 16, width: "90%", maxWidth: 500 };
